/* LAV TDMS — server-side list contract.
   Registers are paginated/sorted/filtered ON THE SERVER (peak: 50k–200k rows);
   the client never loads a whole register. Shared by the API client and the
   MSW mock so the contract is identical when the real Spring Boot API lands. */

export interface SortSpec {
  id: string
  desc: boolean
}

export interface ListQuery {
  /** 0-based page index */
  page: number
  pageSize: number
  sort?: SortSpec[]
  /** columnId -> allowed values (OR within a column, AND across columns) */
  filters?: Record<string, string[]>
  /** global search */
  q?: string
}

export interface Page<T> {
  rows: T[]
  total: number
  page: number
  pageSize: number
  pageCount: number
}

export const DEFAULT_PAGE_SIZE = 25

export function toSearchParams(query: ListQuery): URLSearchParams {
  const p = new URLSearchParams()
  p.set('page', String(query.page))
  p.set('pageSize', String(query.pageSize))
  if (query.q) p.set('q', query.q)
  if (query.sort?.length) {
    p.set('sort', query.sort.map((s) => `${s.id}:${s.desc ? 'desc' : 'asc'}`).join(','))
  }
  for (const [col, vals] of Object.entries(query.filters ?? {})) {
    if (vals.length) p.set(`f.${col}`, vals.join(','))
  }
  return p
}

export function fromSearchParams(sp: URLSearchParams): ListQuery {
  const page = Number(sp.get('page') ?? '0') || 0
  const pageSize = Number(sp.get('pageSize') ?? String(DEFAULT_PAGE_SIZE)) || DEFAULT_PAGE_SIZE
  const sortRaw = sp.get('sort')
  const sort: SortSpec[] = sortRaw
    ? sortRaw
        .split(',')
        .filter(Boolean)
        .map((s) => {
          const [id, dir] = s.split(':')
          return { id: id ?? '', desc: dir === 'desc' }
        })
    : []
  const filters: Record<string, string[]> = {}
  for (const [k, v] of sp.entries()) {
    if (k.startsWith('f.') && v) filters[k.slice(2)] = v.split(',')
  }
  const q = sp.get('q')
  return { page, pageSize, sort, filters, q: q || undefined }
}
